// =============================================================================
// OELOX — Restaurant wallet (credits) client helpers
// =============================================================================

import { getSupabaseClient } from "./index";

export type WalletTransaction = {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  currency: string;
  order_id: string | null;
  note: string | null;
  created_at: string;
};

export type WalletPackage = {
  id: string;
  name: string;
  credits: number;
  price_iqd: number;
  currency: string;
  description: string | null;
  sort_order: number;
};

export type WalletFeeRule = {
  name: string;
  min_order_value: number;
  max_order_value: number | null;
  cost_iqd: number;
  currency: string;
};

export type TenantWallet = {
  balance: number;
  currency: string;
  block_orders_when_no_credits: boolean;
  min_topup_iqd: number;
  transactions: WalletTransaction[];
  packages: WalletPackage[];
  fee_rules: WalletFeeRule[];
};

function asNumber(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function mapWallet(raw: Record<string, unknown>): TenantWallet {
  const txs = Array.isArray(raw.transactions) ? raw.transactions : [];
  const pkgs = Array.isArray(raw.packages) ? raw.packages : [];
  const rules = Array.isArray(raw.fee_rules) ? raw.fee_rules : [];

  return {
    balance: asNumber(raw.balance),
    currency: String(raw.currency || "IQD"),
    block_orders_when_no_credits: Boolean(raw.block_orders_when_no_credits ?? true),
    min_topup_iqd: asNumber(raw.min_topup_iqd, 25000),
    transactions: txs.map((t) => {
      const row = t as Record<string, unknown>;
      return {
        id: String(row.id),
        type: String(row.type),
        amount: asNumber(row.amount),
        balance_after: asNumber(row.balance_after),
        currency: String(row.currency || "IQD"),
        order_id: row.order_id ? String(row.order_id) : null,
        note: row.note ? String(row.note) : null,
        created_at: String(row.created_at || ""),
      };
    }),
    packages: pkgs.map((p) => {
      const row = p as Record<string, unknown>;
      return {
        id: String(row.id),
        name: String(row.name),
        credits: asNumber(row.credits),
        price_iqd: asNumber(row.price_iqd),
        currency: String(row.currency || "IQD"),
        description: row.description ? String(row.description) : null,
        sort_order: asNumber(row.sort_order),
      };
    }),
    fee_rules: rules.map((r) => {
      const row = r as Record<string, unknown>;
      return {
        name: String(row.name),
        min_order_value: asNumber(row.min_order_value),
        max_order_value:
          row.max_order_value == null || row.max_order_value === ""
            ? null
            : asNumber(row.max_order_value),
        cost_iqd: asNumber(row.cost_iqd),
        currency: String(row.currency || "IQD"),
      };
    }),
  };
}

export async function getTenantWallet(tenantId: string): Promise<TenantWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_tenant_wallet", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  return mapWallet((data || {}) as Record<string, unknown>);
}

export async function requestTenantCreditTopup(
  tenantId: string,
  packageId: string
): Promise<{
  ok: boolean;
  payment_id: string;
  credits: number;
  amount_iqd: number;
  status: string;
}> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("request_tenant_credit_topup", {
    p_tenant_id: tenantId,
    p_package_id: packageId,
  });
  if (error) throw error;
  const row = (data || {}) as Record<string, unknown>;
  return {
    ok: Boolean(row.ok),
    payment_id: String(row.payment_id || ""),
    credits: asNumber(row.credits),
    amount_iqd: asNumber(row.amount_iqd),
    status: String(row.status || "pending"),
  };
}

// =============================================================================
// Multi-wallet v2 — client portal (balance, limits, platform fees)
// =============================================================================

export type ClientWalletV2Transaction = {
  id: string;
  order_id: string | null;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
};

export type ClientWalletV2Snapshot = {
  wallet: {
    balance: number;
    currency: string;
    negative_credit_limit: number;
    alert_threshold_iqd: number;
    updated_at: string | null;
  };
  transactions: ClientWalletV2Transaction[];
};

export type TenantBillingSummary = {
  payer: "client" | "partner";
  partner_id: string | null;
  partner_name: string | null;
  per_trip_fee_iqd: number;
  alert_threshold_iqd: number;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Real client wallet (WalletsSpec v2): balance, limits and last 50 txns. */
export async function getTenantClientWallet(
  tenantId: string
): Promise<ClientWalletV2Snapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("client_wallet_get", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const root = asRecord(data);
  const wallet = asRecord(root.wallet);
  const txns = Array.isArray(root.transactions) ? root.transactions : [];
  return {
    wallet: {
      balance: asNumber(wallet.balance),
      currency: String(wallet.currency || "IQD"),
      negative_credit_limit: asNumber(wallet.negative_credit_limit),
      alert_threshold_iqd: asNumber(wallet.alert_threshold_iqd, 10000),
      updated_at: wallet.updated_at ? String(wallet.updated_at) : null,
    },
    transactions: txns.map((t) => {
      const row = asRecord(t);
      return {
        id: String(row.id),
        order_id: row.order_id ? String(row.order_id) : null,
        type: String(row.type || ""),
        amount: asNumber(row.amount),
        balance_after: asNumber(row.balance_after),
        description: row.description ? String(row.description) : null,
        created_at: String(row.created_at || ""),
      };
    }),
  };
}

/** Who pays for this tenant's trips, and at what per-trip fee (no hardcoding). */
export async function getTenantBillingSummary(
  tenantId: string
): Promise<TenantBillingSummary> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("tenant_billing_summary", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const row = asRecord(data);
  return {
    payer: String(row.payer) === "partner" ? "partner" : "client",
    partner_id: row.partner_id ? String(row.partner_id) : null,
    partner_name: row.partner_name ? String(row.partner_name) : null,
    per_trip_fee_iqd: asNumber(row.per_trip_fee_iqd),
    alert_threshold_iqd: asNumber(row.alert_threshold_iqd, 10000),
  };
}
